import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { pushStatsService } from '../../services/pushStatsService';
import {
  IPushDailyStat,
  IPushFilters,
  IPushStatsFilterData,
  PushSource,
} from '../../types/pushStats';
import DeviceStatsCard from './DeviceStatsCard';
import PushStatsChart, { usePushColors } from './PushStatsChart';
import {
  MAX_RANGE_DAYS,
  SortKey,
  addDays,
  applyFilterChange,
  buildCsv,
  daysBetween,
  defaultFilters,
  filtersAreDefault,
  formatLongDate,
  peakDay,
  percentChange,
  previousRange,
  sortRows,
  sumStats,
  todayInLondon,
  totalOf,
  weekdayShort,
} from './pushStatsUtils';

const PRESETS = [7, 30, 90];
const CONTROL_HEIGHT = 40;
const fmt = (n: number) => n.toLocaleString('en-GB');

const controlSx = { '& .MuiInputBase-root': { height: CONTROL_HEIGHT } };
const toggleSx = { height: CONTROL_HEIGHT, '& .MuiToggleButton-root': { px: 2, textTransform: 'none' } };

interface DeltaProps {
  change: number | null;
  /** Lower is better (failures, no recipients). */
  invert?: boolean;
  points?: boolean;
}

const Delta: React.FC<DeltaProps> = ({ change, invert, points }) => {
  const theme = useTheme();
  if (change === null || Math.abs(change) < 0.05) {
    return <Typography component="span" variant="caption" color="text.disabled">—</Typography>;
  }
  const up = change > 0;
  const good = up !== !!invert;
  const text = `${up ? '▲' : '▼'} ${up ? '+' : '−'}${Math.abs(change).toFixed(1)}${points ? ' pts' : '%'}`;
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ fontWeight: 500, color: good ? theme.palette.success.main : theme.palette.error.main }}
    >
      {text}
    </Typography>
  );
};

interface TileProps {
  label: string;
  value: string;
  scope: string;
  swatch?: string;
  delta?: React.ReactNode;
  caption?: string;
  sx?: SxProps<Theme>;
}

const Tile: React.FC<TileProps> = ({ label, value, scope, swatch, delta, caption, sx }) => (
  <Paper sx={[{ p: 2, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', fontSize: 13, fontWeight: 500 }}>
      {swatch && <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: swatch }} />}
      {label}
    </Box>
    <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 500, my: 0.75, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
    <Box sx={{ minHeight: 20 }}>
      {delta}
      {caption && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: delta ? 0.5 : 0 }}>
          {caption}
        </Typography>
      )}
    </Box>
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
      {scope}
    </Typography>
  </Paper>
);

const PushDashboardPage: React.FC = () => {
  const colors = usePushColors();

  const [range, setRange] = useState(() => {
    const to = todayInLondon();
    return { from: addDays(to, -29), to };
  });
  const [preset, setPreset] = useState<number | null>(30);
  const [filters, setFilters] = useState<IPushFilters>(defaultFilters);
  const [filterData, setFilterData] = useState<IPushStatsFilterData>({ apiKeys: [], notificationTypes: [], coupons: [] });
  const [rows, setRows] = useState<IPushDailyStat[]>([]);
  const [previousRows, setPreviousRows] = useState<IPushDailyStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [hoverFromChart, setHoverFromChart] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const requestId = useRef(0);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    pushStatsService
      .getFilterData()
      .then(setFilterData)
      .catch((e) => console.error('Error loading push stats filter data:', e));
  }, []);

  const rangeLength = daysBetween(range.from, range.to);
  const rangeInvalid = rangeLength < 1 || rangeLength > MAX_RANGE_DAYS;

  const load = useCallback(async () => {
    if (rangeInvalid) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const prev = previousRange(range.from, range.to);
      const [current, previous] = await Promise.all([
        pushStatsService.getDailyStats({ ...filters, startDate: range.from, endDate: range.to }),
        pushStatsService.getDailyStats({ ...filters, startDate: prev.from, endDate: prev.to }),
      ]);
      if (id !== requestId.current) return;
      setRows(current);
      setPreviousRows(previous);
    } catch (e) {
      if (id !== requestId.current) return;
      setError(typeof e === 'string' ? e : 'Failed to load push notification stats');
      setRows([]);
      setPreviousRows([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [filters, range, rangeInvalid]);

  useEffect(() => {
    load();
  }, [load]);

  const current = useMemo(() => sumStats(rows), [rows]);
  const previous = useMemo(() => sumStats(previousRows), [previousRows]);
  const peak = useMemo(() => peakDay(rows), [rows]);
  const zeroDays = useMemo(() => rows.filter((r) => totalOf(r) === 0).length, [rows]);
  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  // Keep the hovered table row visible when the hover comes from the chart.
  useEffect(() => {
    if (!hoverDate || !hoverFromChart || !tableRef.current) return;
    const row = tableRef.current.querySelector<HTMLElement>(`[data-date="${hoverDate}"]`);
    const box = tableRef.current;
    if (row && (row.offsetTop < box.scrollTop + 40 || row.offsetTop > box.scrollTop + box.clientHeight - 60)) {
      box.scrollTop = row.offsetTop - box.clientHeight / 2;
    }
  }, [hoverDate, hoverFromChart]);

  const changeFilters = (change: Partial<IPushFilters>) => {
    setFilters((f) => applyFilterChange(f, change, filterData.apiKeys));
    setSelectedDate(null);
  };

  const applyPreset = (days: number) => {
    const to = todayInLondon();
    setRange({ from: addDays(to, -(days - 1)), to });
    setPreset(days);
    setSelectedDate(null);
  };

  const changeDate = (field: 'from' | 'to', value: string) => {
    if (!value) return;
    setRange((r) => ({ ...r, [field]: value }));
    setPreset(null);
    setSelectedDate(null);
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(-1);
    }
  };

  const selectDate = (date: string, scrollToRow: boolean) => {
    setSelectedDate((d) => (d === date ? null : date));
    if (scrollToRow && tableRef.current) {
      const row = tableRef.current.querySelector<HTMLElement>(`[data-date="${date}"]`);
      if (row) tableRef.current.scrollTop = row.offsetTop - tableRef.current.clientHeight / 2;
    }
  };

  const activeFilterLabels = useMemo(() => {
    const labels: string[] = [];
    if (filters.source !== 'all') labels.push(`${filters.source} only`);
    const key = filterData.apiKeys.find((k) => k.apiKeyId === filters.apiKeyId);
    if (key) labels.push(key.name);
    if (filters.notificationType) labels.push(filters.notificationType);
    const coupon = filterData.coupons.find((c) => c.couponId === filters.couponId);
    if (coupon) labels.push(coupon.description);
    return labels;
  }, [filters, filterData]);

  const exportCsv = () => {
    const summary = activeFilterLabels.length ? activeFilterLabels.join(', ') : 'none';
    const blob = new Blob([buildCsv(sortedRows, summary)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `push-daily-${range.from}_${range.to}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const typeOptions = useMemo(() => {
    const key = filterData.apiKeys.find((k) => k.apiKeyId === filters.apiKeyId);
    return key ? key.notificationTypes : filterData.notificationTypes;
  }, [filterData, filters.apiKeyId]);

  const filterNotes = [
    (filters.apiKeyId || filters.notificationType) &&
      'Coupon pushes are not tied to an API key or type, so they are excluded while that filter is on.',
    filters.couponId && 'Custom pushes have no coupon, so they are excluded while a coupon is selected.',
  ].filter(Boolean) as string[];

  const tableHeads: { key: SortKey; group: string; label: string; color?: string }[] = [
    { key: 'customSent', group: 'Custom', label: 'Sent', color: colors.custom },
    { key: 'customFailed', group: 'Custom', label: 'Failed', color: colors.custom },
    { key: 'customNoRecipients', group: 'Custom', label: 'No recipients', color: colors.custom },
    { key: 'couponSent', group: 'Coupon*', label: 'Sent', color: colors.coupon },
    { key: 'total', group: ' ', label: 'Total' },
  ];

  const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
  const rateOf = (r: { customSent: number; customFailed: number }) =>
    r.customSent ? `${((r.customFailed / r.customSent) * 100).toFixed(1)}%` : '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }}>
        Push Notifications – Daily
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {formatLongDate(range.from)} – {formatLongDate(range.to)} · {rangeLength} day{rangeLength === 1 ? '' : 's'}
        {activeFilterLabels.length > 0 && ` · ${activeFilterLabels.join(' · ')}`}
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={filters.source}
            aria-label="Source"
            sx={toggleSx}
            onChange={(_e, value: PushSource | null) => value && changeFilters({ source: value })}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
            <ToggleButton value="coupon">Coupon</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="API key (custom)"
            value={filters.apiKeyId}
            onChange={(e) => changeFilters({ apiKeyId: e.target.value })}
            sx={{ ...controlSx, minWidth: 180, flex: '1 1 180px' }}
          >
            <MenuItem value="">All API keys</MenuItem>
            {filterData.apiKeys.map((k) => (
              <MenuItem key={k.apiKeyId} value={k.apiKeyId}>{k.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Notification type (custom)"
            value={filters.notificationType}
            onChange={(e) => changeFilters({ notificationType: e.target.value })}
            sx={{ ...controlSx, minWidth: 200, flex: '1 1 200px' }}
          >
            <MenuItem value="">All types</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Coupon (coupon)"
            value={filters.couponId}
            onChange={(e) => changeFilters({ couponId: e.target.value })}
            sx={{ ...controlSx, minWidth: 200, flex: '1 1 200px' }}
          >
            <MenuItem value="">All coupons</MenuItem>
            {filterData.coupons.map((c) => (
              <MenuItem key={c.couponId} value={c.couponId}>{c.description}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={preset}
            aria-label="Range"
            sx={toggleSx}
            onChange={(_e, value: number | null) => value && applyPreset(value)}
          >
            {PRESETS.map((days) => (
              <ToggleButton key={days} value={days}>{days}d</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            type="date"
            size="small"
            label="From"
            value={range.from}
            onChange={(e) => changeDate('from', e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: range.to } }}
            sx={{ ...controlSx, width: 160 }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={range.to}
            onChange={(e) => changeDate('to', e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: range.from } }}
            sx={{ ...controlSx, width: 160 }}
          />
          <Box sx={{ flex: 1 }} />
          {!filtersAreDefault(filters) && (
            <Button color="inherit" sx={{ height: CONTROL_HEIGHT }} onClick={() => changeFilters({ ...defaultFilters })}>
              Reset filters
            </Button>
          )}
          <Button variant="outlined" color="secondary" sx={{ height: CONTROL_HEIGHT }} onClick={exportCsv} disabled={!rows.length}>
            Export CSV
          </Button>
        </Box>

        {filterNotes.length > 0 && (
          <Chip
            size="small"
            variant="outlined"
            sx={{ height: 'auto', alignSelf: 'flex-start', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            label={filterNotes.join(' ')}
          />
        )}
        {rangeInvalid && (
          <Alert severity="warning">Pick a range of between 1 and {MAX_RANGE_DAYS} days.</Alert>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ height: 4, mb: 1 }}>{loading && <LinearProgress />}</Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <Tile
          sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
          label="Total sent"
          value={fmt(current.total)}
          delta={<Delta change={percentChange(current.total, previous.total)} />}
          caption="vs prev. period"
          scope="Custom + coupon*"
        />
        <Tile
          label="Custom push"
          swatch={colors.custom}
          value={fmt(current.custom)}
          delta={<Delta change={percentChange(current.custom, previous.custom)} />}
          caption="vs prev."
          scope="Devices targeted"
        />
        <Tile
          label="Coupon push*"
          swatch={colors.coupon}
          value={fmt(current.coupon)}
          delta={<Delta change={percentChange(current.coupon, previous.coupon)} />}
          caption="vs prev."
          scope="Customers, first send only"
        />
        <Tile
          label="Failed"
          value={fmt(current.failed)}
          delta={<Delta invert change={percentChange(current.failed, previous.failed)} />}
          caption="vs prev."
          scope="Custom push only*"
        />
        <Tile
          label="Success rate"
          value={current.successRate === null ? '—' : `${current.successRate.toFixed(1)}%`}
          delta={
            <Delta
              points
              change={
                current.successRate !== null && previous.successRate !== null
                  ? current.successRate - previous.successRate
                  : null
              }
            />
          }
          caption="vs prev."
          scope="Custom push only*"
        />
        <Tile
          label="No recipients"
          value={fmt(current.noRecipients)}
          delta={<Delta invert change={percentChange(current.noRecipients, previous.noRecipients)} />}
          caption="vs prev."
          scope="Custom pushes with no registered device*"
        />
        <Tile
          sx={{ gridColumn: { lg: 'span 2' } }}
          label="Peak day"
          value={peak ? fmt(totalOf(peak)) : '—'}
          caption={peak ? formatLongDate(peak.date) : undefined}
          scope={`avg ${fmt(Math.round(current.total / (rows.length || 1)))}/day${zeroDays ? ` · ${zeroDays} zero day${zeroDays > 1 ? 's' : ''}` : ''}`}
        />
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Sent per day</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: 12, color: 'text.secondary' }}>
            <Legend color={colors.custom} label="Custom" />
            <Legend color={colors.coupon} label="Coupon*" />
            <Legend color={colors.failed} label="Failed (custom only)" ring />
          </Box>
        </Box>
        <PushStatsChart
          rows={rows}
          hoverDate={hoverDate}
          selectedDate={selectedDate}
          onHover={(date) => {
            setHoverFromChart(true);
            setHoverDate(date);
          }}
          onSelect={(date) => selectDate(date, true)}
        />
      </Paper>

      <DeviceStatsCard />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Daily breakdown</Typography>
          <Typography variant="caption" color="text.secondary">
            {range.from} → {range.to} · click a row to mark it on the chart
          </Typography>
        </Box>
        <TableContainer ref={tableRef} sx={{ maxHeight: 520, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ left: 0, zIndex: 4 }} sortDirection={sortKey === 'date' ? (sortDir === 1 ? 'asc' : 'desc') : false}>
                  <Box component="span" sx={{ display: 'block', fontSize: 10 }}>&nbsp;</Box>
                  <TableSortLabel active={sortKey === 'date'} direction={sortDir === 1 ? 'asc' : 'desc'} onClick={() => toggleSort('date')}>
                    Date
                  </TableSortLabel>
                </TableCell>
                {tableHeads.map((h) => (
                  <TableCell
                    key={h.key}
                    align="right"
                    sortDirection={sortKey === h.key ? (sortDir === 1 ? 'asc' : 'desc') : false}
                    sx={{ boxShadow: h.color ? `inset 0 -2px 0 ${h.color}` : undefined }}
                  >
                    <Box component="span" sx={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled' }}>
                      {h.group}
                    </Box>
                    <TableSortLabel active={sortKey === h.key} direction={sortDir === 1 ? 'asc' : 'desc'} onClick={() => toggleSort(h.key)}>
                      {h.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((r) => {
                const failHigh = r.customSent > 0 && r.customFailed / r.customSent > 0.05;
                const zero = (v: number) => (v === 0 ? 'text.disabled' : 'text.primary');
                return (
                  <TableRow
                    key={r.date}
                    data-date={r.date}
                    hover
                    selected={r.date === selectedDate || r.date === hoverDate}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => selectDate(r.date, false)}
                    onMouseEnter={() => {
                      setHoverFromChart(false);
                      setHoverDate(r.date);
                    }}
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      {r.date}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                        {weekdayShort(r.date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ ...numCell, color: zero(r.customSent) }}>{fmt(r.customSent)}</TableCell>
                    <TableCell align="right" sx={{ ...numCell, color: zero(r.customFailed) }}>
                      {fmt(r.customFailed)}
                      {rateOf(r) && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: failHigh ? 'error.main' : 'text.disabled', fontWeight: failHigh ? 600 : 400 }}>
                          {rateOf(r)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numCell, color: zero(r.customNoRecipients) }}>{fmt(r.customNoRecipients)}</TableCell>
                    <TableCell align="right" sx={{ ...numCell, color: zero(r.couponSent) }}>{fmt(r.couponSent)}</TableCell>
                    <TableCell align="right" sx={{ ...numCell, fontWeight: 600, color: zero(totalOf(r)) }}>{fmt(totalOf(r))}</TableCell>
                  </TableRow>
                );
              })}
              {!sortedRows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No data for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {sortedRows.length > 0 && (
              <TableFooter>
                <TableRow sx={{ '& td': { position: 'sticky', bottom: 0, bgcolor: 'background.paper', fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
                  <TableCell sx={{ left: 0, zIndex: 3 }}>Total · {rows.length} days</TableCell>
                  <TableCell align="right" sx={numCell}>{fmt(current.custom)}</TableCell>
                  <TableCell align="right" sx={numCell}>
                    {fmt(current.failed)}
                    {current.custom > 0 && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                        {((current.failed / current.custom) * 100).toFixed(1)}%
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={numCell}>{fmt(current.noRecipients)}</TableCell>
                  <TableCell align="right" sx={numCell}>{fmt(current.coupon)}</TableCell>
                  <TableCell align="right" sx={numCell}>{fmt(current.total)}</TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ color: 'text.disabled', fontSize: 12, px: 0.5 }}>
        <p style={{ margin: '0 0 4px' }}>
          * Coupon push is counted once per customer, on the first send only; re-sends are not counted and no
          delivery-failure data is recorded for coupon pushes. <strong>Failed</strong> and{' '}
          <strong>Success rate</strong> therefore cover custom push only. Custom push counts devices targeted.
        </p>
        <p style={{ margin: '0 0 4px' }}>
          <strong>No recipients</strong> = custom pushes whose audience had no registered device.{' '}
          <strong>Total</strong> = custom sent + coupon sent (failures are not added).
        </p>
        <p style={{ margin: 0 }}>
          Deltas compare against the preceding period of equal length. Days are in Europe/London time.
        </p>
      </Box>
    </Box>
  );
};

const Legend: React.FC<{ color: string; label: string; ring?: boolean }> = ({ color, label, ring }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: ring ? '50%' : '2px',
        bgcolor: ring ? 'transparent' : color,
        border: ring ? `2px solid ${color}` : 'none',
        boxSizing: 'border-box',
      }}
    />
    {label}
  </Box>
);

export default PushDashboardPage;
